import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://bhavishyavani.org";
const DEFAULT_IMAGE = `${SITE}/brand/logo-512.png`;

/** Removes the static #canonical-link injected in index.html so only Helmet's canonical remains — prevents duplicate <link rel="canonical"> tags. */
function useCleanStaticCanonical() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.getElementById("canonical-link");
    if (el) el.parentNode && el.parentNode.removeChild(el);
  }, []);
}

/**
 * Reusable SEO component.
 * Props: title, description, path (starts with /), image, type (website|article|product), keywords, jsonLd (object or array), noIndex, lang, hreflangAlternates
 * hreflangAlternates: [{ lang: "hi", path: "/hi/..." }, ...] — used for multilingual pages
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  keywords,
  jsonLd,
  noIndex = false,
  lang = "en",
  hreflangAlternates = [],
}) {
  useCleanStaticCanonical();
  // Only append the brand suffix if the caller didn't already include it.
  // This keeps homepage titles clean (no "Bhavishyavani | Bhavishyavani").
  const fullTitle = title
    ? (/bhavishyavani/i.test(title) ? title : `${title} | Bhavishyavani`)
    : "Bhavishyavani — AI Vedic Astrology · Kundli · Palmistry · Tarot";
  const desc =
    description ||
    "Instant AI-crafted Vedic astrology reports — Kundli, Palmistry, Tarot, Numerology & more at ₹699 each. PDF delivered to your inbox in seconds.";
  const canonical = `${SITE}${path}`;
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const ogLocale = lang === "hi" ? "hi_IN" : "en_IN";

  return (
    <Helmet prioritizeSeoTags htmlAttributes={{ lang: lang === "hi" ? "hi" : "en" }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {!noIndex && <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />}

      {/* Open Graph */}
      <meta property="og:site_name" content="Bhavishyavani" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Hreflang */}
      {hreflangAlternates.length === 0 && (
        <link rel="alternate" hrefLang={lang === "hi" ? "hi-in" : "en-in"} href={canonical} />
      )}
      {hreflangAlternates.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang === "hi" ? "hi-in" : "en-in"} href={`${SITE}${a.path}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={lang === "hi" && hreflangAlternates.length ? `${SITE}${hreflangAlternates.find(a => a.lang === "en")?.path || path}` : canonical} />

      {/* Structured data */}
      {ldArray.map((ld) => {
        const t = ld["@type"] || "ld";
        const k = Array.isArray(t) ? t.join("-") : t;
        return <script key={k} type="application/ld+json">{JSON.stringify(ld)}</script>;
      })}
    </Helmet>
  );
}

/* ---------- Reusable schema builders ---------- */

export const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Bhavishyavani",
  alternateName: "Bhavishyavani.org",
  url: SITE,
  logo: DEFAULT_IMAGE,
  description:
    "AI-powered Vedic astrology, palmistry, tarot and numerology reports at ₹699 each. Instant PDF delivered.",
  sameAs: [],
  contactPoint: {
    "@type": "ContactPoint",
    email: "reports@bhavishyavani.org",
    contactType: "customer service",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi"],
  },
};

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Bhavishyavani",
  url: SITE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/reports?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function productLd({ title, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description,
    image: DEFAULT_IMAGE,
    brand: { "@type": "Brand", name: "Bhavishyavani" },
    url: `${SITE}${path}`,
    offers: {
      "@type": "Offer",
      price: "699",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: `${SITE}${path}`,
    },
  };
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((i, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: i.name,
      item: `${SITE}${i.path}`,
    })),
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleLd({ title, description, path, published, updated, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image: image || DEFAULT_IMAGE,
    author: { "@type": "Organization", name: "Bhavishyavani" },
    publisher: {
      "@type": "Organization",
      name: "Bhavishyavani",
      logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    },
    datePublished: published,
    dateModified: updated || published,
    mainEntityOfPage: `${SITE}${path}`,
  };
}

/** SoftwareApplication schema — turns the Kundli calculator / Predictions
 *  dashboard into rankable "app" rich-results in Google. */
export function softwareApplicationLd({
  name = "Bhavishyavani Vedic Kundli",
  description = "AI-powered Vedic astrology platform: deterministic Kundli, living dasha, transit-aware wealth & career scoring, opportunity calendar and grounded Ask-AI.",
  path = "/",
  category = "LifestyleApplication",
  price = "0",
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    applicationCategory: category,
    operatingSystem: "Web",
    url: `${SITE}${path}`,
    image: DEFAULT_IMAGE,
    offers: {
      "@type": "Offer",
      price,
      priceCurrency: "INR",
    },
    publisher: { "@type": "Organization", name: "Bhavishyavani" },
  };
}

/** HowTo schema — for "How to cast your Vedic Kundli" step-by-step guides. */
export function howToLd({ name, description, path, steps, totalTimeMinutes = 2 }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    totalTime: `PT${totalTimeMinutes}M`,
    image: DEFAULT_IMAGE,
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: `${SITE}${path}#step-${i + 1}`,
    })),
  };
}

/** Speakable schema — flags snippets suitable for Google Assistant / voice. */
export function speakableLd(cssSelectors = ["h1", "[data-speakable]"]) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: cssSelectors,
    },
  };
}
